/**
 * SLA Register — every service-level commitment the contract agent extracted,
 * Asana-style: one clean expandable row per SLA with its timeframe, target,
 * and stated consequence; expand for the complete verbatim contract language.
 *
 * Derivation happens server-side, deterministically, from the quote-grounded
 * obligation catalog of each contract's latest completed review — a row can
 * only exist where a verbatim quote exists, and "no penalty stated" is shown
 * honestly rather than hidden. Tracking an SLA adopts it into the delegated
 * function set the monitoring calendar builds from (audited).
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const PARTY_CFG = {
  vendor:  { label: 'Vendor',  cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  sponsor: { label: 'Plan',    cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  both:    { label: 'Both',    cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  other:   { label: 'Other',   cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

function SlaRow({ sla, contractId, onTracked }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const party = PARTY_CFG[sla.party] || PARTY_CFG.other;

  const track = async (e) => {
    e.stopPropagation();
    if (busy || sla.tracked) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/slas/track`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          obligation: sla.obligation, timeframe: sla.timeframe,
          benchmark: sla.benchmark, consequence: sla.consequence,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not track the SLA');
      onTracked?.();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50/60">
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${sla.consequence ? 'bg-amber-500' : 'bg-sky-400'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{sla.obligation}</span>
            {sla.timeframe && (
              <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 whitespace-nowrap">
                {sla.timeframe}
              </span>
            )}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${party.cls}`}>{party.label}</span>
            {sla.consequence
              ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">penalty stated</span>
              : <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-50 text-slate-400 border border-slate-200">no penalty stated</span>}
          </div>
          {sla.topic && <div className="text-[11px] text-slate-400 mt-0.5">{sla.topic}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {sla.tracked ? (
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">✓ Tracking</span>
          ) : (
            <button type="button" onClick={track} disabled={busy}
              className="text-[11px] font-semibold text-orange-700 border border-orange-300 rounded-lg px-2.5 py-1 hover:bg-orange-50 disabled:opacity-50">
              {busy ? 'Tracking…' : 'Track'}
            </button>
          )}
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-9 space-y-2.5 text-sm border-t border-slate-100 pt-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">The commitment · verbatim from the contract</div>
            <blockquote className="border-l-2 border-slate-300 pl-3 italic text-slate-600 text-[13px] whitespace-pre-wrap">{sla.quote}</blockquote>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Benchmark</div>
            {sla.benchmark ? (
              <>
                <p className="text-[13px] text-slate-700">{sla.benchmark}</p>
                {sla.benchmark_quote && sla.benchmark_quote !== sla.quote && (
                  <blockquote className="border-l-2 border-indigo-200 pl-3 italic text-slate-500 text-[12px] mt-1">{sla.benchmark_quote}</blockquote>
                )}
              </>
            ) : (
              <p className="text-[12px] text-slate-400">None stated in the contract.</p>
            )}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">If not met</div>
            {sla.consequence ? (
              <>
                <p className="text-[13px] text-amber-800">{sla.consequence}</p>
                {sla.consequence_quote && sla.consequence_quote !== sla.quote && (
                  <blockquote className="border-l-2 border-amber-200 pl-3 italic text-slate-500 text-[12px] mt-1">{sla.consequence_quote}</blockquote>
                )}
              </>
            ) : (
              <p className="text-[12px] text-slate-400">No consequence stated in the contract — worth knowing during oversight.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SlaRegister({ contractId = null, embedded = false }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const url = contractId ? `${API}/api/fdr/slas?contract_id=${contractId}` : `${API}/api/fdr/slas`;
      const res = await fetch(url, { credentials: 'include' });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Failed');
      setGroups(d.groups || []);
      setError('');
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-slate-400 px-1 py-3">Loading SLAs…</p>;
  if (error) return <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>;

  const total = groups.reduce((n, g) => n + g.slas.length, 0);
  if (total === 0) {
    return (
      <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
        No SLAs on file yet. SLAs appear here automatically after the contract agent reviews a contract — run a review from the Contracts tab.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groups.filter(g => g.slas.length > 0).map(g => (
        <div key={g.contract_id} className="space-y-2">
          {!embedded && (
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm font-bold text-slate-800">{g.entity_name}</span>
              <span className="text-xs text-slate-400">· {g.contract_title || g.contract_number}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">SLAs · {g.slas.length}</span>
            </div>
          )}
          {embedded && (
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">SLAs & timeframes · {g.slas.length}</div>
          )}
          {g.slas.map((sla, i) => (
            <SlaRow key={`${g.contract_id}-${i}`} sla={sla} contractId={g.contract_id} onTracked={load} />
          ))}
          {g.other_obligations > 0 && (
            <p className="text-[11px] text-slate-400 pl-1">
              Plus {g.other_obligations} obligation{g.other_obligations === 1 ? '' : 's'} without a stated timeframe — all visible under Additional Obligations in the contract review. Nothing is hidden by this view.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
